import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { isBefore } from 'date-fns'
import { Autocomplete, Button, Select } from '@chilecompra/react-kit/components'
import {
  DOWNLOADS_BUCKETS_DICTIONARY,
  DOWNLOADS_REPORT_TYPE_OPTIONS,
  DOWNLOADS_SEARCH_PROVIDER_OPTIONS,
  DOWNLOADS_SEARCH_SECTOR_OPTIONS,
  ENABLED_DOWNLOADS_SEARCH_TYPE_OPTIONS,
  DOWNLOADS_SEMESTER_OPTIONS
} from '../../config/settings/constants'
import {
  BuildIcon,
  Clipboard,
  ClipboardAction,
  ClipboardIcon,
  ClipboardText,
  Description,
  GridContainer,
  GridItem,
  Info,
  InfoText,
  Label,
  PreviewIcon,
  Strong
} from './DownloadOrdersAndBiddingsSection.styles'

const SEARCH_TYPES_DICTIONARY = {
  entcode: 'entcode',
  sector: 'sector',
  tamano: 'tamano'
}

/**
 * The Reports's component.
 */
const Reports = props => {
  const {
    biddingOptions,
    orderOptions,
    reportTypeValue,
    searchBuyerOption,
    searchBuyerOptions,
    searchBuyerValue,
    searchProviderValue,
    searchSectorValue,
    searchTypeValue,
    semesterValue,
    yearValue,
    yearsOptions,
    onChangeReportType,
    onChangeSearchBuyer,
    onChangeSearchProvider,
    onChangeSearchSector,
    onChangeSearchType,
    onChangeSemester,
    onChangeYear,
    onClickDownload,
    onDoubleClickClipboard,
    onOpenBuildDialog,
    onOpenPreviewDialog,
    onSelectSearchBuyer
  } = props
  const fileName = useMemo(() => {
    let fileName = '[NombreArchivo]'

    if (searchTypeValue === SEARCH_TYPES_DICTIONARY.entcode && !!searchBuyerOption?.value) {
      fileName = searchBuyerOption?.value || ''
    }

    if (searchTypeValue === SEARCH_TYPES_DICTIONARY.sector && !!searchSectorValue) {
      fileName = searchSectorValue
    }

    if (searchTypeValue === SEARCH_TYPES_DICTIONARY.tamano && !!searchProviderValue) {
      fileName = searchProviderValue
    }

    return fileName
  }, [searchBuyerOption, searchProviderValue, searchSectorValue, searchTypeValue])
  const downloadUrl = useMemo(
    () =>
      `https://${DOWNLOADS_BUCKETS_DICTIONARY[reportTypeValue] || '[TipoReporte]'}.mercadopublico.cl/${
        searchTypeValue || '[TipoBusqueda]'
      }/${yearValue || '[Año]'}/${semesterValue || '[Semestre]'}/${fileName || '[entcode]'}.7z`,
    [fileName, reportTypeValue, semesterValue, yearValue]
  )
  const updatedYearOptions = useMemo(() => {
    if (searchTypeValue !== SEARCH_TYPES_DICTIONARY.entcode) {
      return yearsOptions
    }

    return reportTypeValue === 'oc-da' ? orderOptions : biddingOptions
  }, [reportTypeValue, searchTypeValue])
  const updatedSemesterOptions = useMemo(() => {
    if (searchTypeValue !== SEARCH_TYPES_DICTIONARY.entcode) {
      const currentYear = new Date().getFullYear()

      return DOWNLOADS_SEMESTER_OPTIONS.filter(
        (_, index) => !(currentYear === yearValue && index === 1 && isBefore(new Date(), new Date(currentYear, 6, 1)))
      )
    }

    const found = updatedYearOptions.find(item => item.value === yearValue) || {}

    return [
      found.semester1 && { name: 'Primer semestre', value: 'Sem1' },
      found.semester2 && { name: 'Segundo semestre', value: 'Sem2' }
    ].filter(Boolean)
  }, [searchTypeValue, updatedYearOptions, yearValue])
  const cannotChangeReportType = useMemo(() => {
    if (searchTypeValue === SEARCH_TYPES_DICTIONARY.entcode) {
      return !searchBuyerOption?.value
    }

    if (searchTypeValue === SEARCH_TYPES_DICTIONARY.sector) {
      return !searchSectorValue
    }

    return !searchProviderValue
  }, [searchBuyerOption, searchProviderValue, searchSectorValue, searchTypeValue])
  const cannotSubmit = useMemo(
    () => fileName === '[NombreArchivo]' || !reportTypeValue || !semesterValue || !yearValue,
    [fileName, reportTypeValue, semesterValue, yearValue]
  )

  return (
    <>
      <GridContainer padding="0 0 27px">
        <GridItem xs={12}>
          <Description>
            Utiliza los filtros y descarga archivos comprimidos con formato .7z que contienen los reportes de órdenes de
            compra y/o licitaciones. Los reportes de órdenes de compra están separados por mecanismo de compra, y los de
            licitaciones separados por mes.
            <br />
            <br />
            <Strong>
              La información estará disponible para aquellos organismos compradores que cuenten con datos, considerando
              que hay organismos que en ciertos años y/o semestres no cuentan con datos de transacciones.
            </Strong>
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 6px" spacing={1}>
        <GridItem xs={12} md={4}>
          <Select
            options={ENABLED_DOWNLOADS_SEARCH_TYPE_OPTIONS}
            value={searchTypeValue}
            onChange={onChangeSearchType}
          />
        </GridItem>
        <GridItem xs={12} md={8}>
          {searchTypeValue === SEARCH_TYPES_DICTIONARY.entcode && (
            <Autocomplete
              option={searchBuyerOption}
              options={searchBuyerOptions}
              placeholder="Ingresa el nombre del organismo, por ejemplo: “Municipalidad de Santiago”"
              value={searchBuyerValue}
              onChange={onChangeSearchBuyer}
              onSelect={onSelectSearchBuyer}
            />
          )}
          {searchTypeValue === SEARCH_TYPES_DICTIONARY.sector && (
            <Select
              options={DOWNLOADS_SEARCH_SECTOR_OPTIONS}
              value={searchSectorValue}
              onChange={onChangeSearchSector}
            />
          )}
          {searchTypeValue === SEARCH_TYPES_DICTIONARY.tamano && (
            <Select
              options={DOWNLOADS_SEARCH_PROVIDER_OPTIONS}
              value={searchProviderValue}
              onChange={onChangeSearchProvider}
            />
          )}
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 15px" spacing={1}>
        <GridItem xs={12} md={4}>
          <Select
            disabled={cannotChangeReportType}
            label="TIPO DE REPORTE"
            options={DOWNLOADS_REPORT_TYPE_OPTIONS}
            value={reportTypeValue}
            onChange={onChangeReportType}
          />
        </GridItem>
        <GridItem xs={12} md={4}>
          <Select
            disabled={!reportTypeValue}
            label="AÑO"
            options={updatedYearOptions}
            tooltip="Sólo se muestran los años en que el organismo comprador registra datos"
            value={yearValue}
            onChange={onChangeYear}
          />
        </GridItem>
        <GridItem xs={12} md={4}>
          <Select
            disabled={!reportTypeValue || !yearValue}
            label="SEMESTRE"
            options={updatedSemesterOptions}
            tooltip="Sólo se muestran los semestres en que el organismo comprador registra datos"
            value={semesterValue}
            onChange={onChangeSemester}
          />
        </GridItem>
      </GridContainer>
      <GridContainer alignItems="flex-end" spacing={1}>
        <GridItem xs={12} md={8}>
          <Label>Copia la siguiente URL en tu navegador o descarga directamente el archivo</Label>
          <Clipboard onDoubleClick={onDoubleClickClipboard(downloadUrl)}>
            <ClipboardText>{downloadUrl}</ClipboardText>
            <ClipboardAction>
              <ClipboardIcon />
            </ClipboardAction>
          </Clipboard>
        </GridItem>
        <GridItem align="right" xs={12} md={4}>
          <Button color="primary" disabled={cannotSubmit} onClick={onClickDownload(fileName, downloadUrl)}>
            Descargar archivo
          </Button>
        </GridItem>
      </GridContainer>
      <GridContainer spacing={1}>
        <GridItem xs={8}>
          <Info onClick={onOpenBuildDialog}>
            <BuildIcon />
            <InfoText>Conoce como se construye esta URL</InfoText>
          </Info>
          <Info onClick={onOpenPreviewDialog}>
            <PreviewIcon />
            <InfoText>Vista previa del reporte</InfoText>
          </Info>
        </GridItem>
        <GridItem xs={4} />
      </GridContainer>
    </>
  )
}

Reports.propTypes = {
  biddingOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ).isRequired,
  orderOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ).isRequired,
  reportTypeValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  searchBuyerOption: PropTypes.shape({
    name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  searchBuyerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ).isRequired,
  searchBuyerValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  searchProviderValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  searchSectorValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  searchTypeValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  semesterValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  yearValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  yearsOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ).isRequired,
  onChangeReportType: PropTypes.func.isRequired,
  onChangeSearchBuyer: PropTypes.func.isRequired,
  onChangeSearchProvider: PropTypes.func.isRequired,
  onChangeSearchSector: PropTypes.func.isRequired,
  onChangeSearchType: PropTypes.func.isRequired,
  onChangeSemester: PropTypes.func.isRequired,
  onChangeYear: PropTypes.func.isRequired,
  onClickDownload: PropTypes.func.isRequired,
  onDoubleClickClipboard: PropTypes.func.isRequired,
  onOpenBuildDialog: PropTypes.func.isRequired,
  onOpenPreviewDialog: PropTypes.func.isRequired,
  onSelectSearchBuyer: PropTypes.func.isRequired
}

export default Reports
